// src/modules/app-views/_apps/appmulti/services/appmultiService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { clone, normalize, onProduction, sort } from 'utils/helpers';
import dayjs from 'dayjs';

import {
  MdOutlineCalendarToday,
  MdOutlineElderly,
  MdOutlineLuggage,
  MdOutlineMedicalServices,
  MdPerson,
} from 'react-icons/md';
import axiosService from 'services/axiosService';
import financialService from 'services/financialService';
import appService from 'services/appService';
import orderService from 'services/orderService';
import receivableService from 'services/receivableService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import { calculatePlanValues, getTravelGrievanceLevel } from './pricingService';
import couponService from 'services/couponService';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
let parameters;
let _loadParametersPromise = null;
const appmultiService = {};

// Test-only hook to seed/clear the parameters singleton.
export const __setParametersForTest = doc => {
  parameters = doc;
  _loadParametersPromise = null;
};

// *******************************************************************************
// PARAMETERS SINGLETON
// *******************************************************************************
appmultiService.loadParameters = async () => {
  if (parameters) return parameters;
  if (_loadParametersPromise) return _loadParametersPromise;

  _loadParametersPromise = (async () => {
    try {
      const axiosOptions = {
        url: '/v1/appmulti/parameters',
        returnAsObject: true,
      };
      const [doc] = await axiosService.get(axiosOptions);
      parameters = doc;
      return doc;
    } finally {
      _loadParametersPromise = null;
    }
  })();

  return _loadParametersPromise;
};

// *******************************************************************************
// OPTION GETTERS
// *******************************************************************************
appmultiService.getApis = () => {
  return parameters?.apis?.map(d => ({
    value: d,
    label: d.charAt(0).toUpperCase() + d.slice(1),
    disabled: false,
  }));
};

appmultiService.getDestinations = titleFromLabel => {
  const destinations = onProduction()
    ? parameters?.destinations?.filter(x => x.value !== 'test')
    : parameters?.destinations;

  return destinations
    ?.map(d => {
      const text = translateX(d.text);
      const label = translateX(d.label);

      return {
        value: d.value,
        text: normalize(text, 'lower'),
        label: titleFromLabel ? text : label,
        tooltipText: translateX(d.tooltipText),
        disabled: false,
      };
    })
    ?.sort((a, b) => sort(a, b, 'text'));
};

appmultiService.getCurrencies = titleFromLabel => {
  return parameters?.currencies?.map(d => {
    const text = translateX(d.text);
    const label = translateX(d.label);

    return {
      value: d.value,
      text: normalize(text, 'lower'),
      label: titleFromLabel ? text : label,
      tooltipText: translateX(d.tooltipText),
      disabled: false,
    };
  });
};

appmultiService.getPriceTypes = titleFromLabel => {
  return parameters?.priceTypes?.map(d => {
    const text = translateX(d.text);
    const label = translateX(d.label);

    return {
      value: d.value,
      text: normalize(text, 'lower'),
      label: titleFromLabel ? text : label,
      tooltipText: translateX(d.tooltipText),
      disabled: false,
    };
  });
};

appmultiService.getTravelProfiles = titleFromLabel => {
  return parameters?.travelProfiles
    ?.map(d => {
      const text = translateX(d.text);
      const label = translateX(d.label);

      return {
        value: d.value,
        text: normalize(text, 'lower'),
        label: titleFromLabel ? text : label,
        tooltipText: translateX(d.tooltipText),
        disabled: false,
      };
    })
    .sort((a, b) => sort(a, b, 'text'));
};

appmultiService.getGrievanceLevels = titleFromLabel => {
  return parameters?.grievance
    ?.map(d => {
      const text = translateX(d.text);
      const label = translateX(d.label);

      return {
        value: d.value,
        text: normalize(text, 'lower'),
        label: titleFromLabel ? text : label,
        tooltipText: translateX(d.tooltipText),
        disabled: false,
      };
    })
    .sort((a, b) => sort(a, b, 'text'));
};

appmultiService.getCoverages = titleFromLabel => {
  return parameters?.coverages
    ?.map(d => {
      const text = translateX(d.text);
      const label = translateX(d.label);

      return {
        value: d.value,
        text: normalize(text, 'lower'),
        label: titleFromLabel ? text : label,
        tooltipText: translateX(d.tooltipText),
        disabled: false,
        highlight: d.highlight,
        type: d.type,
      };
    })
    .sort((a, b) => sort(a, b, 'text'));
};

appmultiService.getOperatorsFromPlans = plans => {
  const arr = plans?.map(p => {
    return {
      value: p?.__operator?._id,
      text: p?.__operator?.name,
      label: p?.__operator?.name,
      tooltipText: p?.__operator?.name,
      disabled: false,
    };
  });

  const operatorsArr = arr?.filter(
    (obj, index, array) => array.findIndex(item => item.value === obj.value) === index
  );

  return operatorsArr?.sort((a, b) => sort(a, b, 'text'));
};

// *******************************************************************************
// PLANS FETCH + DECORATE + SORT
// *******************************************************************************
appmultiService.getPlans = async (destiny, travelPeriodArr, passengersArr, highlightStyle) => {
  const departure = travelPeriodArr?.[0]?.format('YYYY-MM-DD');
  const arrival = travelPeriodArr?.[1]?.format('YYYY-MM-DD');
  const days =
    departure && arrival ? dayjs(travelPeriodArr[1]).diff(travelPeriodArr[0], 'days') + 1 : 0;

  if (
    !destiny ||
    !departure ||
    !arrival ||
    !days ||
    !passengersArr ||
    !Array.isArray(passengersArr) ||
    passengersArr.length <= 0
  ) {
    return;
  }

  const options = { url: API.PLANS.GET_BY_DESTINY };

  options.qs = { destiny, passengers: passengersArr, days };
  const [data] = await axiosService.get(options);

  const docs = clone(data);

  const plansArr = docs?.map(doc =>
    appmultiService.decoratePlan({
      plan: doc,
      days,
      passengersArr,
      highlightStyle,
    })
  );

  return appmultiService.sorterPlans(plansArr);
};

appmultiService.decoratePlan = ({
  plan,
  days,
  passengersArr,
  highlightStyle = 'operator',
  adminDiscount,
  adminAdjustment,
}) => {
  if (plan?.priceType === 'table') {
    const pday = plan?.priceTable?.find(t => t.from <= days && t.to >= days);
    plan.price = pday?.value || 0;
  }

  plan.summary = calculatePlanValues({
    plan,
    days,
    passengersArr,
    adminDiscount,
    adminAdjustment,
  });

  plan.highlights = [];

  if (highlightStyle.includes('client')) {
    let highlight = plan?.coverages?.find(c => c?.coverageKey === 'health_care');
    if (highlight) {
      plan.highlights.push({
        icon: <MdOutlineMedicalServices />,
        title: translateX(highlight.coverageKey),
        content: financialService.formatMoney({
          value: highlight?.amount || 0,
          fix: 2,
          localeSymbol: highlight?.currency || 'brl',
        }),
      });
    }

    highlight = plan?.coverages?.find(c => c?.coverageKey === 'luggage_loss');
    if (highlight) {
      plan.highlights.push({
        icon: <MdOutlineLuggage />,
        title: translateX(highlight.coverageKey),
        content: financialService.formatMoney({
          value: highlight?.amount || 0,
          fix: 2,
          localeSymbol: highlight?.currency || 'brl',
        }),
      });
    }
  }

  const _p = plan.summary.parameters;
  const rdPrice = plan.summary.resellerDefaultPrice;
  const rg1Price = plan.summary.resellerGrievance1Price;
  const rg2Price = plan.summary.resellerGrievance2Price;
  const rg3Price = plan.summary.resellerGrievance3Price;

  if (highlightStyle.includes('operator')) {
    plan.highlights.push({
      icon: <MdOutlineCalendarToday />,
      title: translateX('days'),
      content: days < _p.days ? `${days} (${translateX('min')} ${_p.days})` : days,
    });

    plan.highlights.push($buildGrievanceLevel(0));

    if (['level1', 'level2', 'level3'].includes(plan.grievanceLevel)) {
      plan.highlights.push($buildGrievanceLevel(1));
    }

    if (['level2', 'level3'].includes(plan.grievanceLevel)) {
      plan.highlights.push($buildGrievanceLevel(2));
    }

    if (['level3'].includes(plan.grievanceLevel)) {
      plan.highlights.push($buildGrievanceLevel(3));
    }
  }

  return plan;

  ////

  function $buildGrievanceLevel(level, profile = 'pricePerPassenger') {
    let dailyPriceTo;
    let grievancePriceTo;

    let minAge = 0;
    let icon = <MdOutlineElderly />;
    let title;

    switch (level) {
      case 1:
        minAge = _p.grievance1Ages[0];
        dailyPriceTo = rg1Price.netDailyPrice;
        grievancePriceTo = rg1Price.netTotalPrice;

        title = `${minAge}+ ${translateX('years_old')} ${rg1Price.qty ? `(${rg1Price.qty})` : ''}`;
        break;

      case 2:
        minAge = _p.grievance2Ages[0];
        dailyPriceTo = rg2Price.netDailyPrice;
        grievancePriceTo = rg2Price.netTotalPrice;

        title = `${minAge}+ ${translateX('years_old')} ${rg2Price.qty ? `(${rg2Price.qty})` : ''}`;
        break;

      case 3:
        minAge = _p.grievance3Ages[0];
        dailyPriceTo = rg3Price.netDailyPrice;
        grievancePriceTo = rg3Price.netTotalPrice;

        title = `${minAge}+ ${translateX('years_old')} ${rg3Price.qty ? `(${rg3Price.qty})` : ''}`;
        break;

      default:
        icon = <MdPerson />;
        dailyPriceTo = rdPrice.netDailyPrice;
        grievancePriceTo = rdPrice.netTotalPrice;

        title = `${translateX('price_per_traveler')} ${rdPrice.qty ? `(${rdPrice.qty})` : ''}`;
    }

    const passengerPriceTo = dailyPriceTo * _p.days;
    let priceTo;

    switch (profile) {
      case 'pricePerDay':
        priceTo = dailyPriceTo;
        break;

      case 'pricePerGrievance':
        priceTo = grievancePriceTo;
        break;

      case 'pricePerPassenger':
      default:
        priceTo = passengerPriceTo;
        break;
    }

    priceTo = financialService.formatMoney({
      value: priceTo || 0,
      fix: 2,
      localeSymbol: 'brl',
    });

    return {
      icon,
      title,
      content: <span>{priceTo}</span>,
    };
  }
};

appmultiService.sorterPlans = (plans, sorterObj) => {
  const operator = sorterObj?.operator || null;
  const profile = sorterObj?.profile || null;
  const classification = sorterObj?.classification || 'best_seller';

  let plansSorted = plans;
  let idx;

  if (operator) {
    plansSorted = plansSorted?.filter(p => p?.__operator?._id === operator);
  }

  if (profile) {
    plansSorted = plansSorted?.filter(p => p.travelProfiles?.includes(profile));
  }

  plansSorted?.map(p => {
    p.bestSeller = false;
    p.cheaper = false;
    p.moreExpansive = false;
    return p;
  });

  const rankingOrders = plansSorted?.sort((a, b) => sort(b, a, 'rankingOrders'))?.[0];

  const bestSellerId = rankingOrders?.rankingOrders > 0 ? rankingOrders._id : undefined;

  if (bestSellerId) {
    idx = plansSorted?.findIndex(p => p._id === bestSellerId);
    if (idx > -1) plansSorted[idx].bestSeller = true;
  }

  const allValuesAreEqual = plansSorted?.every((item, index) => {
    if (index === 0) return true;
    return item?.summary?.total?.totalToPay === plansSorted[0]?.summary?.total?.totalToPay;
  });

  const cheaperId = plansSorted?.sort(
    (a, b) => a?.summary?.total?.totalToPay - b?.summary?.total?.totalToPay
  )?.[0]?._id;
  if (cheaperId && plansSorted.length > 1 && !allValuesAreEqual) {
    idx = plansSorted?.findIndex(p => p._id === cheaperId);
    if (idx > -1) plansSorted[idx].cheaper = true;
  }

  const moreExpansiveId = plansSorted?.sort(
    (a, b) => b?.summary?.total?.totalToPay - a?.summary?.total?.totalToPay
  )?.[0]?._id;
  if (moreExpansiveId && plansSorted.length > 1 && !allValuesAreEqual) {
    idx = plansSorted?.findIndex(p => p._id === moreExpansiveId);
    if (idx > -1) plansSorted[idx].moreExpansive = true;
  }

  switch (classification) {
    case 'best_seller': {
      plansSorted = plansSorted
        ?.sort((a, b) => a.summary.total.totalToPay - b.summary.total.totalToPay)
        ?.sort((a, b) => b.bestSeller - a.bestSeller);
      break;
    }

    case 'cheaper': {
      plansSorted = plansSorted
        ?.sort((a, b) => a.summary.total.totalToPay - b.summary.total.totalToPay)
        ?.sort((a, b) => sort(a, b, 'moreExpansive'));
      break;
    }

    case 'more_expansive': {
      plansSorted = plansSorted
        ?.sort((a, b) => b.summary.total.totalToPay - a.summary.total.totalToPay)
        ?.sort((a, b) => sort(a, b, 'cheaper'));
      break;
    }

    default:
  }

  return plansSorted;
};

// *******************************************************************************
// COVERAGE VALUE FORMATTER
// *******************************************************************************
appmultiService.getCoverageValue = (record, returnAsComponent = false) => {
  switch (record?.type) {
    case 'money': {
      const value = financialService.formatMoney(record?.amount, 2, record?.currency, true);
      return returnAsComponent ? <div>{value}</div> : value;
    }

    case 'switch': {
      const value = !!record?.included;
      return returnAsComponent ? null : value;
    }

    case 'text':
    default: {
      const value = record?.text;
      return returnAsComponent ? <div>{value || '-'}</div> : value;
    }
  }
};

// *******************************************************************************
// TRAVELLER AGGREGATION
// *******************************************************************************
appmultiService.reduceTravellerQtyAndPrices = (
  travellers,
  level = 0,
  totalObj = 'valuesInVoucherDate'
) => {
  const { qty, price } = travellers?.reduce(
    (accumulator, currentValue) => {
      if (currentValue.grievanceLevel === level) {
        accumulator.qty += 1;
        accumulator.price += currentValue[totalObj].resellerPrice;
      }
      return accumulator;
    },
    { qty: 0, price: 0 }
  ) || { qty: 0, price: 0 };

  return {
    qty,
    price,
  };
};

// *******************************************************************************
// API LOGS
// *******************************************************************************
appmultiService.getApiLogs = async () => {
  const options = {
    url: `/v1/api-logs/get-list`,
  };
  const [docs] = await axiosService.get(options);

  const data = clone(docs);

  return data?.map(doc => {
    if (doc?.createdAt) {
      doc.createdAt = dayjs(doc.createdAt);
    }
    return doc;
  });
};

// *******************************************************************************
// GRIEVANCE LEVEL (delegate to pricingService)
// *******************************************************************************
appmultiService.getTravelGrievanceLevel = (plan, age) => getTravelGrievanceLevel(plan, age);

// *******************************************************************************
// ORDER WRITER (POST order; bill/receivable deferred to Phase 1d-iv)
// *******************************************************************************
appmultiService.generateOrderFromQuotation = async (
  filter,
  plan,
  travellers,
  buyer,
  emergencyContacts = [],
  billingType // selected payment method — consumed by 1d-iv bill call (see seam)
) => {
  const f = filter;
  const p = plan;
  const _s = plan?.summary || {};
  const _p = plan?.summary?.parameters || {};

  const b = buyer;
  const t = travellers || [];

  let _indice = `${b.name} | ${b.cpf} | ${b.email} | ${b.phone} `;

  t.forEach(x => {
    _indice += `| ${x.name} `;
    if (x.cpf) _indice += `| ${x.cpf} `;
  });

  const _parameters = {
    currency: _p.currency,
    dailyPrice: _p.dailyPrice, // in dollar
    exchangeRate: _p.exchangeRate,
    grievance1: _p.grievance1,
    grievance2: _p.grievance2,
    grievance3: _p.grievance3,
    markdown: _p.markdown,
    discount: _p.discount,
    minDays: _p.minDays,
    maxDays: _p.maxDays,
    minAge: _p.minAge,
    maxAge: _p.maxAge,
    grievanceLevel: _p.grievanceLevel,
    grievance1Ages: _p.grievance1Ages,
    grievance2Ages: _p.grievance2Ages,
    grievance3Ages: _p.grievance3Ages,
  };

  const body = {
    _indice,

    destiny: f.destiny,
    departure: dayjs(f.departure),
    arrival: dayjs(f.arrival),
    days: f.days,
    minDays: _p.days,
    buyerId: b._id,
    // map FE 'region' (SimpleAddressForm standard) → backend 'province' (buyerAddressSchema)
    // __city / addressMetadata / addressCoordinates are UI-only metadata — not sent
    buyerAddress: {
      zip: b.zip,
      address1: b.address1,
      number: b.number,
      address2: b.address2,
      neighborhood: b.neighborhood,
      city: b.city,
      province: b.region,
      country: b.country,
    },
    planId: p._id,
    operatorId: p.operatorId,

    parametersInOrderDate: { ..._parameters },
    parametersInVoucherDate: { ..._parameters },

    emergencyContacts,

    travellers: t.map(traveller => {
      let providerPrice;
      let resellerPrice;
      let grievancePercentage;

      switch (traveller.grievanceLevel) {
        case 3:
          providerPrice = _s?.providerGrievance3Price?.netTotalPrice || 0;
          resellerPrice = _s?.resellerGrievance3Price?.netTotalPrice || 0;
          grievancePercentage = p?.grievanceAdditionAge3;
          break;
        case 2:
          providerPrice = _s?.providerGrievance2Price?.netTotalPrice || 0;
          resellerPrice = _s?.resellerGrievance2Price?.netTotalPrice || 0;
          grievancePercentage = p?.grievanceAdditionAge2;
          break;
        case 1:
          providerPrice = _s?.providerGrievance1Price?.netTotalPrice || 0;
          resellerPrice = _s?.resellerGrievance1Price?.netTotalPrice || 0;
          grievancePercentage = p?.grievanceAdditionAge1;
          break;
        case 0:
        default:
          providerPrice = _s?.providerDefaultPrice?.netTotalPrice || 0;
          resellerPrice = _s?.resellerDefaultPrice?.netTotalPrice || 0;
          grievancePercentage = 0;
          break;
      }

      return {
        entityId: traveller.entityId,
        ageInTravelDate: traveller.age,
        grievanceLevel: traveller.grievanceLevel || 0,
        grievancePercentage,
        valuesInOrderDate: { providerPrice, resellerPrice },
        valuesInVoucherDate: { providerPrice, resellerPrice },
      };
    }),

    totalInOrderDate: _s.total,
    totalInVoucherDate: _s.total,

    // 1) 'waiting_for_payment:warning' -> aguardando pagamento
    // 2) 'waiting_for_voucher:processing' -> pago, aguardando voucher
    // 3) 'done:success' -> voucher gerado
    // 4) 'cancelled:error' -> vencido/nao pago
    status: 'waiting_for_payment',
    voucherDate: null,
    saleOrigin: 'local',
  };

  // POST ORDER — singular /v1/order backend route (Phase 1b)
  const [order] = await axiosService.post({
    url: API.APPMULTI_ORDER.URL.replace(':_id?', ''),
    data: body,
  });

  if (!order) {
    appService.notification({
      flag: 'e',
      title: 'order',
      description: 'order_generation_error_description',
      duration: 5,
    });
    return null;
  }

  appService.notification({
    flag: 's',
    title: 'order',
    description: 'order_generation_success_description',
    duration: 5,
  });

  // Redeem coupon (Option B) — fire-and-forget, never blocks order success
  const _coupon = plan?.summary?.total?.couponDiscount;
  if (order?._id && _coupon?.couponId) {
    (async () => {
      try {
        await couponService.consume(_coupon.couponId, {
          orderId: order._id,
          discountValue: _coupon.discountValue,
        });
      } catch {
        // consume failure is graceful — order already created
      }
    })();
  }

  // 1d-iv: generate the asaas bill/payment for the just-created order.
  // Graceful: a billing failure must NOT lose the order — the order stays
  // 'waiting_for_payment' with no payment link (matches a backup pre-billing order).
  let bill = null;
  try {
    bill = await receivableService.generateBillFromOrder(
      order._id,
      b._id,
      billingType,
      undefined,
      true
    );
  } catch {
    // billing failure is graceful — order already created, no payment link attached
  }

  // fire-and-forget — backend send-email handler is Phase 2; never block the flow
  orderService.sendEmail(order?._id, 'created').catch(() => {});

  const decorated = await orderService.getOrderById(order?._id);
  // attach the bill FE-side so Step 3 renders the payment link without
  // backend order decoration (Phase-1 order GET does not hydrate __receivables)
  if (decorated && bill) decorated.__receivables = [bill];
  return decorated;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default appmultiService;
